'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Check, ClipboardList, Loader2, MapPin, Ruler } from 'lucide-react';
import { useEstimateIntro } from '../hooks/use-estimate-intro';
import {
  useConceptEstimates,
  selectCurrentEstimate,
} from '../hooks/use-estimate';
import { EstimateEditor } from './EstimateEditor';
import { EstimateDraftSettings } from './EstimateDraftSettings';
import { useSubscriptionLimits } from '@/features/subscription';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import { ProBadge } from '@/features/subscription/ui/ProBadge';
import { SettingsCard } from '@/shared/ui/settings/SettingsCard';
import { NestedSheetStackProvider, NoticeBanner } from '@/shared/ui';
import { Button } from '@/shared/ui/shadcn/button';
import { cn } from '@/shared/lib/cn';

interface EstimateTabContentProps {
  projectId: string;
  conceptId: string;
  /** Stacked (single-column) layout: page-flow spacing instead of the fixed
   *  inspector column. Mirrors the album/render panel `isStacked` switch. */
  isStacked?: boolean;
  /** Two-column layout: the settings list + primary CTA live in the inspector
   *  aside (EstimateSettingsPanel) — the content column must not render its
   *  inline copies (applies to the editor AND the working intro). */
  settingsHostedExternally?: boolean;
}

/**
 * Estimate tab.
 *
 * Three states:
 * - No estimate yet, NOT entitled → server-driven marketing intro (FE1):
 *   hero + region/units context + benefit bullets; Generate opens the Pro
 *   Pass paywall (cost estimate is a Pro perk gated by `has_cost_estimate`).
 * - No estimate yet, entitled → WORKING intro: hero + the same settings group
 *   the completed estimate shows, backed by an in-memory draft
 *   (EstimateDraftSettings); Generate POSTs the draft. On the two-column
 *   layout the settings + Generate live in the inspector aside
 *   (EstimateSettingsPanel) — only the hero stays here.
 * - An estimate exists → the editor (FE2): header + Materials/Labor sections,
 *   inline-editable line items, regenerate. The editor shows the "current"
 *   estimate (last ready, else most recent) — the full carousel is FE3.
 *
 * All marketing copy (intro) and business messages (failure reason, paywall)
 * come from the API; the client only owns static chrome labels.
 */
export function EstimateTabContent({
  projectId,
  conceptId,
  isStacked = false,
  settingsHostedExternally = false,
}: EstimateTabContentProps) {
  const t = useTranslations('common.estimate');
  const locale = useLocale();

  const { data: intro, isLoading: isLoadingIntro, isError, refetch } =
    useEstimateIntro(projectId, conceptId);

  const {
    data: estimates,
    isLoading: isLoadingEstimates,
  } = useConceptEstimates(projectId, conceptId);

  const limits = useSubscriptionLimits();
  const hasCostEstimate = limits?.has_cost_estimate ?? false;
  const openPassPaywall = usePassPaywallStore((s) => s.open);

  const currentEstimate = selectCurrentEstimate(estimates);

  // An estimate exists → render the editor. The NestedSheetStackProvider +
  // `relative` container host the project-goals sheet opened from the
  // facade-area link: two-column slides it over the estimate column, stacked
  // takes the full-viewport overlay (same switch as the render/album panels).
  if (currentEstimate) {
    return (
      <NestedSheetStackProvider isStacked={isStacked}>
        <div className={cn('relative flex flex-col', !isStacked && 'h-full')}>
          <EstimateEditor
            conceptId={conceptId}
            projectId={projectId}
            estimateId={currentEstimate.id}
            locale={locale}
            settingsHostedExternally={settingsHostedExternally}
          />
        </div>
      </NestedSheetStackProvider>
    );
  }

  const measurementLabel =
    intro?.measurement_system === 'imperial'
      ? t('measurement.imperial')
      : t('measurement.metric');

  const isLoading = isLoadingIntro || isLoadingEstimates;

  // No estimate yet → the intro (settings/draft). Cap it at a readable column
  // width and center it on BOTH layouts: an edge-to-edge intro (full-width
  // bullets + a metre-long Generate bar) reads as a stretched placeholder. This
  // is the SETTINGS surface, so it stays narrow — unlike the generated estimate
  // document (the editor branch above), which renders full width.
  return (
    <div
      className={cn(
        'flex flex-col gap-4 mx-auto w-full max-w-[560px]',
        !isStacked && 'h-full'
      )}
    >
      {isLoading && (
        <div className="flex flex-1 items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {isError && (
        <NoticeBanner
          variant="error"
          title={t('loadError')}
          action={
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              {t('retry')}
            </Button>
          }
        />
      )}

      {/* Entitled → WORKING intro: hero + draft settings + Generate (the
          settings/CTA move to the inspector aside on two-column). No ProBadge
          (it marks the lock), no marketing context line / bullets. The
          provider + `relative` container host the drill-down NestedSheets
          (goals / currency / language / requirements) — editor-branch
          parity. */}
      {!isLoading && intro && hasCostEstimate && (
        <NestedSheetStackProvider isStacked={isStacked}>
          <div
            className={cn(
              'relative flex flex-col gap-4',
              !isStacked && 'flex-1'
            )}
          >
            {/* Hero — icon + server-driven heading (Apple `list.clipboard`
                hero parity). */}
            <div className="px-1 pt-2">
              <ClipboardList
                className="h-8 w-8 text-primary"
                strokeWidth={1.5}
              />
              <h2 className="mt-2 text-card-title text-foreground">
                {intro.title}
              </h2>
              <p className="mt-1 text-card-body text-muted-foreground">
                {intro.subtitle}
              </p>
            </div>

            {!settingsHostedExternally && (
              <EstimateDraftSettings
                projectId={projectId}
                conceptId={conceptId}
                intro={intro}
                variant="inline"
              />
            )}
          </div>
        </NestedSheetStackProvider>
      )}

      {/* Not entitled → marketing intro: server-driven benefits; Generate
          upsells the Pro Pass (EntitlementFeatureMap: cost_estimate), never
          the deprecated subscription paywall. */}
      {!isLoading && intro && !hasCostEstimate && (
        <>
          <div className="px-1 pt-2">
            <div className="flex items-center gap-2">
              <h2 className="text-card-title text-foreground">{intro.title}</h2>
              <ProBadge tier="pro" isLocked />
            </div>
            <p className="mt-1 text-card-body text-muted-foreground">
              {intro.subtitle}
            </p>
          </div>

          {/* Region + measurement context — server-resolved geo (region_label
              is optional; measurement system always present). */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-card-body text-muted-foreground">
            {intro.region_label && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
                {intro.region_label}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Ruler className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
              {measurementLabel}
            </span>
          </div>

          {intro.bullets.length > 0 && (
            <SettingsCard>
              <ul className="divide-y divide-border/60">
                {intro.bullets.map((bullet) => (
                  <li
                    key={bullet}
                    className="flex items-start gap-3 px-4 py-3"
                  >
                    <Check
                      className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
                      strokeWidth={2}
                    />
                    <span className="text-card-body text-foreground">
                      {bullet}
                    </span>
                  </li>
                ))}
              </ul>
            </SettingsCard>
          )}

          <div className="flex justify-center">
            <Button
              variant="teal"
              size="cta"
              onClick={() => openPassPaywall('pro')}
            >
              {t('generate')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
